<template>
	<div class="app-table">
		<div class="app-table__head">
			<table class="app-table__table">
				<thead>
					<tr>
						<th
							v-if="checkbox"
							class="app-table__checkbox"
						>
							<label
								class="checkbox"
								:class="{'checkbox--disabled' : isSelectAllDisabled}"
								@click.stop
							>
								<input
									type="checkbox"
									:disabled="isSelectAllDisabled"
									:checked="isAllSelected"
									@change="selectAll"
								>
								<div class="mark"></div>
							</label>
						</th>
						<th
							v-for="(field, index) in fields"
							:key="index"
							:style="field.style"
							class="app-table__field"
						>
							{{ field.text }}
						</th>
					</tr>
				</thead>
			</table>
		</div>
		<div
			class="app-table__body"
			v-on:scroll="handleScroll"
		>
			<table
				class="app-table__table"
				v-if="!!$listeners['end-of-scroll'] ? dataset && dataset.length : !loading"
			>
				<tbody>
					<tr
						class="app-table__row"
						:class="{'app-table__row--selected' : selected && isSelected(item)}"
						v-for="(item, index) in dataset"
						:key="index"
						@click="$emit('click', item)"
					>
						<td
							v-if="checkbox"
							class="app-table__checkbox"
						>
							<label
								class="checkbox"
								@click.stop
							>
								<input
									type="checkbox"
									:checked="selected && isSelected(item)"
									@change="select($event, item)"
								>
								<div class="mark"></div>
							</label>
						</td>

						<td
							class="app-table__cell"
							v-for="(field, index) in fields"
							:style="field.style"
							:key="index"
						>
                            <slot :name="field.name" :props="{item: item}" v-if="field.type === 'number'">
								{{ item[field.name] }}
							</slot>

							<slot :name="field.name" :props="{item: item}" v-else>
								{{ item[field.name] || '' }}
							</slot>
						</td>
					</tr>
				</tbody>
			</table>
			<div
				v-if="loading"
				class="app-table__loading"
				:class="{'app-table__empty' : !!$listeners['end-of-scroll'] ? (!dataset || !dataset.length) : true }"
			>
				<slot name="loading"> Loading ... </slot>
			</div>

			<div v-if="!loading && (!dataset || !dataset.length)" class="app-table__empty">
				<slot name="empty"> No data found </slot>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		dataset: Array,
		fields: Array,
		selected: { type: Array, default: () => [] },
		checkbox: { type: Boolean, default: true },
		loading: Boolean
	},

	computed: {
		isSelectAllDisabled () {
			return this.loading ||
				!this.dataset ||
				!this.dataset.length
		},

		isAllSelected () {
			return !this.loading &&
				this.dataset &&
				this.dataset.length &&
				this.selected &&
				this.dataset.every(item => this.isSelected(item))
		}
	},

	methods: {
		select (event, item) {
			const selected = this.selected
			if (event.target.checked) {
				this.$emit('select', [...selected, item])
			} else {
				this.$emit('select', selected.filter(selectedItem => {
					return JSON.stringify(selectedItem) !== JSON.stringify(item)
				}))
			}
		},

		selectAll (event) {
			if (event.target.checked) {
				const newItems = this.dataset.filter(i => !this.isSelected(i))
				this.$emit('select', [...this.selected, ...newItems])
			} else {
				const newItems = this.selected.filter(item => {
					return !this.dataset.some(data => {
						return JSON.stringify(item) === JSON.stringify(data)
					})
				})
				this.$emit('select', newItems)
			}
		},

		isSelected (item) {
			if (!this.selected.length || !item) return false
			return this.selected.some(i => {
				return JSON.stringify(i) === JSON.stringify(item)
			})
		},

		handleScroll (event) {
			if (!this.$listeners['end-of-scroll']) return
			let e = event.target
			if (e.scrollTop + e.offsetHeight + 10 >= e.scrollHeight) {
				this.$emit('end-of-scroll', event)
			}
		}
	}
}
</script>

<style lang="scss" scoped>
$shade: #F8F9FB;
$shade-dark: #F0F0F7;

.app-table {
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;
	color: $default;
	font-size: 1em;
	border-bottom: 2px solid $shade;
	overflow: hidden;

	&__table {
		width: 100%;
		table-layout: fixed;
		border-collapse: collapse;
	}

	&__head {
		background-color: $shade;
		user-select: none;
		padding-right: .4em; // for scrollbar
	}

	&__body {
		flex: 1 1 auto;
		overflow: auto;
	}

	&__field, &__cell, &__checkbox {
		padding: 1em .5em;
		vertical-align:	text-top;
	}

	&__checkbox {
		width: 2.6em;
		vertical-align:	text-top;

		.checkbox {
			display: flex;
			justify-content: center;
			align-items: center;
			border: 1px solid #E5E8EB;
			background-color: #fff;
			height: 1.3em;
			width: 1.3em;
			border-radius: 3px;
			padding-bottom: .1em;
			cursor: pointer;
			position: relative;

			&:hover { border-color: $primary; }

			input[type="checkbox"] {
				position: absolute;
				appearance: none;
				pointer-events: none;
				outline: none;
				opacity: 0;
			}

			.mark {
				display: block;
				width: .35em;
				height: .6em;
				border: solid #fff;
				border-width: 0 .1em .1em 0;
				transform-origin: center;
				transform: rotate(45deg);
			}

			input[type="checkbox"]:checked ~ .mark { border-color: $primary }

			&--disabled {
				pointer-events: none;
			}
		}
	}

	&__field, &__cell {
		text-align: left;
		font-weight: normal;
		word-break: break-word;
		word-wrap: break-word;
	}

	&__row:hover {
		background-color: #fafafa;

		.checkbox { border-color: $primary; }
	}

	&__row {
		cursor: pointer;
		border-bottom: 1px solid #E6EAF2;

		&--selected,
		&--selected:hover {
			background-color: #F0F8F8;
		}
	}

	&__empty {
		height: 100%;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: $shade-dark;
		color: $default-light;
		user-select: none;
	}

	&__loading {
		display: flex;
		justify-content: center;
		padding: 5px;
	}
}

// laptops
@media screen
  and (min-device-width: 1200px)
  and (-webkit-min-device-pixel-ratio: 1) {
	.app-table {
		&__cell {
			word-wrap: none;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		&__row:hover &__cell {
			word-break: break-word;
			word-wrap: break-word;
			white-space: normal;
		}
	}
}
</style>
